import React, { ReactNode, createContext, useContext, useEffect, useState } from "react";
//import axios from "axios";
//import api from "../API/api";
import { handlesLogin } from "../API/handlesLogin";
import { handleSwitchProfile } from "../API/handleSwitchProfile";
//import { handleLogout } from "../API/handleLogout";
import { AuthResponse, User } from "../types/Models";

const AUTH_TOKEN_KEY = "authToken";
const AUTH_USER_KEY = "authUser";
const AUTH_PAGES_KEY = "authPages";

interface AuthContextType {
  user: User | null;
  getDisplayName: () => string;
  login: (cpf: string, password: string, captchaToken: string) => Promise<AuthResponse>;
  switchProfile: (roleId: number) => Promise<void>;
  logout: () => void;
  isAuthenticated: () => boolean;
  userType: () => string;
  userPageList: string[];
  userActionPermissions: string[];
  hasActionPermission: (permissionKey: string) => boolean;
  requiresPasswordChange: boolean;
  clearRequiredPasswordChange: () => void;
}

//const API_URL = process.env.REACT_APP_API_URL;

export const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(() => {
    const storedUser = sessionStorage.getItem(AUTH_USER_KEY);

    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser) as User;
    } catch {
      sessionStorage.removeItem(AUTH_USER_KEY);
      return null;
    }
  });

  const [userPageList, setPages] = useState<string[]>(() => {
    const storedPages = sessionStorage.getItem(AUTH_PAGES_KEY);

    if (!storedPages) return [];

    try {
      return JSON.parse(storedPages) as string[];
    } catch {
      sessionStorage.removeItem(AUTH_PAGES_KEY);
      return [];
    }
  });

  useEffect(() => {
    if (user && userPageList.length === 0 && user.permissions?.length) {
      const normalizedPermissions = [...new Set(user.permissions)];
      setPages(normalizedPermissions);
      sessionStorage.setItem(AUTH_PAGES_KEY, JSON.stringify(normalizedPermissions));
    }
  }, [user, userPageList.length]);

  const persistAuthState = (nextUser: User | null, pages: string[]) => {
    if (nextUser) {
      sessionStorage.setItem(AUTH_USER_KEY, JSON.stringify(nextUser));
    } else {
      sessionStorage.removeItem(AUTH_USER_KEY);
    }

    if (pages.length > 0) {
      sessionStorage.setItem(AUTH_PAGES_KEY, JSON.stringify(pages));
    } else {
      sessionStorage.removeItem(AUTH_PAGES_KEY);
    }
  };

  const clearRequiredPasswordChange = () => {
    setUser((prevUser) => {
      if (!prevUser) return prevUser;

      const nextUser = {
        ...prevUser,
        force_password_change: false,
      };

      persistAuthState(nextUser, userPageList);
      return nextUser;
    });
  };

  const applyUserData = (userData: AuthResponse, cpf: string) => {
    const normalizedPermissions = [
      ...new Set([...(userData.permissions || []), "logout"]),
    ];

    const mappedUser: User = {
      id: userData.user_id ?? userData.id,
      nome: userData.name || userData.nome || "",
      nome_social: userData.nome_social || "",
      email: userData.email,
      token: userData.token,
      cpf,
      role: userData.role,
      role_id: userData.role_id,
      permissions: normalizedPermissions,
      action_permissions: [...new Set(userData.action_permissions || [])],
      active_role: userData.active_role,
      base_role: userData.base_role,
      switchable_roles: userData.switchable_roles,
      force_password_change: userData.force_password_change === true,
    };

    sessionStorage.setItem(AUTH_TOKEN_KEY, userData.token);
    setUser(mappedUser);
    setPages(normalizedPermissions);
    persistAuthState(mappedUser, normalizedPermissions);
  };

  const login = async (cpf: string, password: string, captchaToken: string): Promise<AuthResponse> => {
    try {
      const userData = await handlesLogin(cpf, password, captchaToken);
      applyUserData(userData, cpf);
      return userData;
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const switchProfile = async (roleId: number): Promise<void> => {
    try {
      const userData = await handleSwitchProfile(roleId);
      applyUserData(userData, user?.cpf || "");
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const logout = async () => {
    try {
      //const response = await handleLogout();
      sessionStorage.removeItem(AUTH_TOKEN_KEY);
      sessionStorage.removeItem(AUTH_USER_KEY);
      sessionStorage.removeItem(AUTH_PAGES_KEY);
      setUser(null);
      setPages([]);
    } catch (error: any) {
      throw new Error(error.message);
    }
  };

  const isAuthenticated = () => {
    const token = sessionStorage.getItem(AUTH_TOKEN_KEY);
    return Boolean(user && token);
  };

  const userType = () => {
    return user?.role || "";
  };

  const getDisplayName = () => {
    return user?.nome_social?.trim() || user?.nome || "";
  };

  const userActionPermissions = user?.action_permissions || [];
  const requiresPasswordChange = user?.force_password_change === true;

  const hasActionPermission = (permissionKey: string) => {
    return userActionPermissions.includes(permissionKey);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        getDisplayName,
        login,
        switchProfile,
        logout,
        isAuthenticated,
        userType,
        userPageList,
        userActionPermissions,
        hasActionPermission,
        requiresPasswordChange,
        clearRequiredPasswordChange,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
